import React from "react";
import PropTypes from "prop-types";
import { kebabCase } from "lodash";
import styled, { css } from "styled-components";
import Link from "next/link";
import { DateTime } from "luxon";

const EmptyTimeSlot = styled.hr`
  display: none;

  ${({ theme }) => theme.mq.lg`
    display: block;
    background-color: ${theme.themeColors.darkGreen3};
    border: none;
    font-weight: 600;
    height: 0.0313rem;
    width: 0.4375rem;
  `}
`;

const TimeSlotItem = styled.li`
  align-items: center;
  display: flex;
  min-height: 2.5rem;
  list-style-type: none;
  margin: 0;
  padding: 0 0.1875rem;
  width: 4.6875rem;

  ${({ isHiddenTimeSlot }) =>
    isHiddenTimeSlot &&
    css`
      border-color: transparent;
      height: 0;
      min-height: 0;
      opacity: 0;
      visibility: hidden;
    `}

  ${({ theme }) => theme.mq.lg`
    justify-content: center;
    padding: 0;
    width: 6.25rem;
  `}
`;

const Pill = styled.a`
  align-items: center;
  background-color: transparent;
  border: 1px ${({ theme }) => theme.themeColors.orange1} solid;
  border-radius: 21px;
  color: ${({ theme }) => theme.themeColors.orange1};
  display: flex;
  font-size: ${({ theme }) => theme.fontSize(0)};
  justify-content: center;
  letter-spacing: 0.3px;
  min-height: 1.625rem;
  opacity: 1;
  text-align: center;
  transition: all 0.3s;
  white-space: nowrap;
  width: 4.6875rem;

  :focus {
    outline: 0;
  }

  :focus-visible {
    outline: 0.125rem solid #0070f3;
  }

  :hover {
    background-color: ${({ theme }) => theme.themeColors.orange1};
    color: ${({ theme }) => theme.themeColors.white};
  }

  span {
    align-items: center;
    align-self: center;
    display: inline-flex;
    font-weight: 400;
    line-height: 0.75rem;
    margin-bottom: -0.125rem;
  }

  ${({ isHiddenTimeSlot }) =>
    isHiddenTimeSlot &&
    css`
      border-color: transparent;
      opacity: 0;
      visibility: hidden;
    `}

  ${({ theme }) => theme.mq.lg`
    flex-direction: column;
  `}
`;

function TimeSlotPill({
  apptDateInMsecs,
  bizId,
  columnDay,
  timeZoneId,
  columnItemIdx,
  isCustomerUser,
  isHiddenTimeSlot,
  isMobile,
  isVendorUser,
  setShowChangeRoleModal,
  timePillLink,
  timePillQueryParams,
  timeStamp,
  showCustomerModalSetPath,
}) {
  // If there is no TimeStamp...
  if (!timeStamp && !isMobile) {
    return (
      <TimeSlotItem
        key={`filler-line-${bizId}-${kebabCase(columnDay)}-${columnItemIdx}`}
      >
        <EmptyTimeSlot />
      </TimeSlotItem>
    );
  }
  
  const tsStartTime = DateTime.fromISO(timeStamp.attributes.pillDisplayStart).setZone(timeZoneId)

  // If there's a customer user and timestamp exists...
  if (isCustomerUser) {
    return (
      <TimeSlotItem
        isHiddenTimeSlot={isHiddenTimeSlot}
        key={`day-pill-${bizId}-${kebabCase(columnDay)}-${columnItemIdx}`}
      >
        <Link
          href={{
            pathname: timePillLink,
            query: {
              ...timePillQueryParams,
              apptDate: apptDateInMsecs,
              providerId: bizId,
            },
          }}
          passHref
        >
          <Pill isHiddenTimeSlot={isHiddenTimeSlot}>
            <span>{tsStartTime.toFormat("h:mm a")}</span>
          </Pill>
        </Link>
      </TimeSlotItem>
    );
  }

  // If there's a vendor user and timestamp exists...
  if (isVendorUser) {
    return (
      <TimeSlotItem
        isHiddenTimeSlot={isHiddenTimeSlot}
        key={`day-pill-${bizId}-${kebabCase(columnDay)}-${columnItemIdx}`}
      >
        <Pill
          as="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowChangeRoleModal(true);
          }}
          isHiddenTimeSlot={isHiddenTimeSlot}
        >
          <span>{tsStartTime.toFormat("h:mm a")}</span>
        </Pill>
      </TimeSlotItem>
    );
  }

  // Not logged in and timestamp exists...
  return (
    <TimeSlotItem
      isHiddenTimeSlot={isHiddenTimeSlot}
      key={`day-pill-${bizId}-${kebabCase(columnDay)}-${columnItemIdx}`}
    >
      <Pill
        as="button"
        onClick={(e) => {
          e.stopPropagation();
          showCustomerModalSetPath(timePillLink, {
            ...timePillQueryParams,
            apptDate: apptDateInMsecs,
            providerId: bizId,
          });
        }}
        isHiddenTimeSlot={isHiddenTimeSlot}
      >
        <span>{tsStartTime.toFormat("h:mm a")}</span>
      </Pill>
    </TimeSlotItem>
  );
}

export default TimeSlotPill;

TimeSlotPill.propTypes = {
  timeStamp: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  bizId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  timePillLink: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  timePillQueryParams: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  isHiddenTimeSlot: PropTypes.bool,
  isMobile: PropTypes.bool,
  timeZoneId: PropTypes.string,
  columnItemIdx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  columnDay: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  isCustomerUser: PropTypes.bool,
  isVendorUser: PropTypes.bool,
  apptDateInMsecs: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setShowChangeRoleModal: PropTypes.func,
  showCustomerModalSetPath: PropTypes.func,
};

TimeSlotPill.defaultProps = {
  timeStamp: null,
  bizId: null,
  timeZoneId: null,
  timePillLink: null,
  timePillQueryParams: {},
  isHiddenTimeSlot: false,
  isMobile: false,
  columnItemIdx: null,
  columnDay: null,
  isCustomerUser: false,
  isVendorUser: false,
  apptDateInMsecs: "",
  setShowChangeRoleModal: () => {},
  showCustomerModalSetPath: () => {},
};
